import React, { Suspense, lazy, useEffect, useState } from 'react';
import { B } from '../constants';
import { destinations as destinationsAPI } from '../api';
import { FullPhoto } from './FallbackImg';

// Loaded on demand: Leaflet is only needed once a sheet is opened.
const MapView = lazy(() => import('./MapView'));

const SERIF = "'Playfair Display',serif";
const SANS = "'Montserrat',sans-serif";

export function haversineKm(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}
const CATEGORY_LABEL = {
  museum: 'Museums', religious: 'Religious sites', viewpoint: 'Viewpoints', beach: 'Beaches',
  park: 'Parks & nature', restaurant: 'Restaurants', cafe: 'Cafés', bar: 'Bars', nightlife: 'Nightlife',
  shopping: 'Shopping', activity: 'Activities', landmark: 'Landmarks', attraction: 'Attractions',
};
export const categoryLabel = (c) => CATEGORY_LABEL[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : 'Other');
export const fmtKm = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`);
export const fmtVisit = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m} min`);

// Bottom sheet (centred dialog on wide screens) shared by the city page's sheets.
export function Sheet({ title, onClose, children, maxWidth = 560 }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div role="dialog" aria-modal="true" aria-label={title} onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(15,25,35,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} className="gb-sheet"
        style={{ background: 'white', width: '100%', maxWidth, maxHeight: '90vh', overflowY: 'auto', borderRadius: '22px 22px 0 0', boxShadow: '0 -10px 40px rgba(15,25,35,0.25)', fontFamily: SANS, position: 'relative' }}>
        <style>{'@media (min-width: 720px) { .gb-sheet { border-radius: 22px !important; margin-bottom: 5vh; } }'}</style>
        <button type="button" onClick={onClose} aria-label="Close"
          style={{ position: 'absolute', top: 10, right: 10, zIndex: 2, width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'rgba(15,25,35,0.65)', color: '#fff', fontSize: 16, cursor: 'pointer' }}>✕</button>
        {children}
      </div>
    </div>
  );
}

// One verified place: its stored description and the lead of its Wikipedia article,
// which is what explains why it is popular. Sections with no data are left out.
export default function PlaceSheet({ place, center, cityName, categoryLabel, onAddToTrip, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (place.id == null || place.id === '') { setDetail({}); return undefined; }
    setDetail(null);
    destinationsAPI.place(place.id)
      .then((r) => { if (!cancelled) setDetail(r); })
      .catch(() => { if (!cancelled) setDetail({}); });
    return () => { cancelled = true; };
  }, [place.id]);

  const p = { ...place, ...(detail?.place || {}) };
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [p.imageUrl]);
  const showImg = !!p.imageUrl && !imgFailed;
  const about = detail?.about;
  const hasCoords = Number.isFinite(p.lat) && Number.isFinite(p.lng);
  const km = p.distanceKm != null ? p.distanceKm
    : (center && hasCoords && Number.isFinite(center.lat) ? haversineKm(center, p) : null);
  const meta = [
    p.category ? (categoryLabel ? categoryLabel(p.category) : p.category) : null,
    p.durationMin ? `⏱ ${fmtVisit(p.durationMin)} typical visit` : null,
    km != null ? `${fmtKm(km)} from ${cityName || 'city'} centre` : null,
  ].filter(Boolean);

  return (
    <Sheet title={p.name} onClose={onClose}>
      {showImg && (
        <div style={{ position: 'relative', height: 240, overflow: 'hidden', borderRadius: '22px 22px 0 0', background: B.night }}>
          <FullPhoto sources={[p.imageUrl]} alt={p.name} onExhausted={() => setImgFailed(true)} />
        </div>
      )}
      <div style={{ padding: showImg ? '18px 20px 22px' : '18px 56px 22px 20px' }}>
        <h3 style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 700, margin: '0 0 6px', color: B.night }}>{p.name}</h3>
        <div style={{ fontSize: 12.5, color: B.muted, marginBottom: 14 }}>{meta.join(' · ')}</div>
        {onAddToTrip && (
          <button type="button" onClick={() => onAddToTrip(p)}
            style={{ marginBottom: 14, padding: '9px 16px', borderRadius: 999, border: 'none', background: B.forest, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
            + Add to trip
          </button>
        )}
        {p.description && (
          <p style={{ fontSize: 14, color: B.night, fontWeight: 600, margin: '0 0 10px', lineHeight: 1.5 }}>
            {p.description.charAt(0).toUpperCase() + p.description.slice(1)}
          </p>
        )}
        {detail == null && <p style={{ fontSize: 13, color: B.muted }}>Loading more about this place…</p>}
        {about?.text && (
          <>
            <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', color: B.forest, margin: '14px 0 6px' }}>Why people go</p>
            <p style={{ fontSize: 14, lineHeight: 1.7, color: '#3b4450', margin: 0 }}>{about.text}</p>
            {about.url && (
              <a href={about.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 13, color: B.forest, fontWeight: 700 }}>
                Read more on {about.source || 'Wikipedia'} →
              </a>
            )}
          </>
        )}
        {hasCoords && (
          <div style={{ marginTop: 16 }}>
            <Suspense fallback={null}><MapView locations={[{ id: p.id, lat: p.lat, lng: p.lng, name: p.name, photoUrl: p.imageUrl || '' }]} height={180} /></Suspense>
          </div>
        )}
      </div>
    </Sheet>
  );
}
